import Box from '@mui/material/Box';
import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useState } from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { messageContext } from "../../stores/messages/messageContext";
import { userContext } from "../../stores/users/userContext";
import { getMyCreationsUrl } from '../constants';
import DownloadButton from "../download/downloadButton";
import Header from "../header/header";
import { myCreationHeader } from "../header/headerConstants";
import Loading from "../loading/loading";
import TextSubTitle from "../subtitles/subtitles";
import { handleSort } from '../../utils';
import Title from "../title/title";
import './mycreations.css';
import { SUPPORTED_VIDEO_FORMATS } from '../constants';


const MyCreations = () => {
    const userStore = useContext(userContext)
    const messageStore = useContext(messageContext)
    const [myCreations, setMyCreations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [header, setHeader] = useState(myCreationHeader)
    const [subtitleTexts, setSubtitleTexts] = useState({});

    const fetchData = async () => {
        try {
            setLoading(true)
            const response = await fetch(`${getMyCreationsUrl}?token=${userStore.getToken()}`)
            const data = await response.json()
            setMyCreations(data)
            if (data.length === 0) {
                messageStore.setMessage("You haven't downloaded or uploaded any videos yet, but feel free to do so using options menu.", 'info', 10000)
            }
            setLoading(false)
        } catch (error) {
            setLoading(false)
            messageStore.setMessage(error)
        }
    }

    const fetchSubtitleText = async (url, id) => {
        try {
            const response = await fetch(url);
            const text = await response.text();
            setSubtitleTexts(prev => ({
                ...prev,
                [id]: text
            }));
        } catch (error) {
            console.error("Error fetching subtitle:", error);
            setSubtitleTexts(prev => ({
                ...prev,
                [id]: "Error loading subtitle"
            }));
        }
    };

    const onSortClick = (index) => {
        handleSort(myCreations, header, index, setMyCreations, setHeader);
    };

    useEffect(() => {
        fetchData()
    }, [])

    useEffect(() => {
        myCreations.forEach(creation => {
            if (creation.ext === 'srt' || creation.ext === 'vtt') {
                fetchSubtitleText(creation.video, creation.id);
            }
        });
    }, [myCreations]);

    if (loading) return <Loading />


    const itemContent = (item, data) => {
        return (
            header.map((obj, index) => {
                if (obj.visible) {
                    if (obj.type === 'video') {
                        if (SUPPORTED_VIDEO_FORMATS.includes(data['ext']))
                            return (
                                <td key={index} className={`${obj.field} ${obj.type}`} data-label="Video">
                                    <video
                                        playsInline
                                        src={data['video']}
                                        controls
                                        controlsList="nodownload nofullscreen"
                                        preload="metadata"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: '8px'
                                        }}
                                        onPlay={(e) => {
                                            // Force controls to stay visible
                                            e.target.setAttribute('controls', 'controls');
                                        }}
                                    />
                                </td>
                            )
                        if (data['ext'] === 'mp3')
                            return (
                                <td key={index} className={`${obj.field} ${obj.type}`} data-label="Audio">
                                    <audio
                                        controls
                                        preload="metadata"
                                        src={data['video']}
                                        style={{
                                            width: '100%',
                                            height: '40px',
                                            display: 'block',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <source src={data['video']} type="audio/mpeg" />
                                        Your browser does not support the audio element.
                                    </audio>
                                </td>
                            )
                        if (data['ext'] === 'gif')
                            return (
                                <td key={index} className={`${obj.field} ${obj.type}`} data-label="GIF">
                                    <img
                                        src={data['video']}
                                        alt="gif creation"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: '8px'
                                        }}
                                    />
                                </td>
                            )
                        if (data['ext'] === 'srt' || data['ext'] === 'vtt')
                            return (
                                <td key={index} className={`${obj.field} ${obj.type}`} data-label="Subtitles">
                                    <div className="subtitle-container">
                                        <TextSubTitle
                                            text={subtitleTexts[data.id] || "Loading subtitle..."}
                                            matchVideo={true}
                                        />
                                    </div>
                                </td>
                            )
                        if (data['ext'] === 'zip')
                            return (
                                <td key={index} className={`${obj.field} ${obj.type}`} data-label="Archive">
                                    <span title="Zip file">
                                        {data['file_name']}
                                    </span>
                                </td>
                            )
                    }
                    if (obj.type === 'download') {
                        // New download column - show download button for all downloadable files
                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} data-label="Download" style={{ textAlign: 'center' }}>
                                <DownloadButton
                                    url={data['video']}
                                    name={data['title'] + '.' + data['ext']}
                                    fileExtension={data['ext']}
                                    inline={true}
                                />
                            </td>
                        )
                    }
                    if (obj.type === 'description')
                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} data-label="Description">
                                <Title title={data[obj.field]} maxWidth={obj.width} />
                            </td>
                        )
                    if (obj.type === 'date')
                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} data-label="Created">
                                {data[obj.field]}
                            </td>
                        )
                    if (obj.type === 'string')
                        return (
                            <td key={index} className={`${obj.field} ${obj.type}`} data-label={obj.title}>
                                <Title title={data[obj.field]} maxWidth={obj.width} />
                            </td>
                        )
                }
                return null
            })
        )
    }


    return (
        <Box sx={{
            width: '100%',
            height: '100vh',
            paddingTop: { xs: '70px', sm: '75px', md: '61px' },
            paddingLeft: { xs: 1, sm: 2, md: 0 },
            paddingRight: { xs: 1, sm: 2, md: 0 },
            paddingBottom: { xs: 1, sm: 2, md: 3 },
            boxSizing: 'border-box',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <TableVirtuoso
                style={{
                    height: '100%',
                    width: '100%',
                    maxWidth: '100%',
                    flex: 1
                }}
                components={{
                    Table: (props) => (
                        <table
                            {...props}
                            className="mycreations-table responsive-table"
                            style={{
                                ...props.style,
                                tableLayout: 'fixed',
                                width: '100%',
                                maxWidth: '100%',
                                borderCollapse: 'collapse'
                            }}
                        />
                    ),
                    TableRow: React.forwardRef((props, ref) => (
                        <tr
                            {...props}
                            ref={ref}
                            className={`${props.className || ''} responsive-table-row`}
                        />
                    ))
                }}
                fixedHeaderContent={() => <Header header={header} onClick={onSortClick} />}
                data={myCreations}
                itemContent={itemContent}
            />
        </Box>
    )
}

export default observer(MyCreations);